import { Link } from "react-router-dom";
import { BookOpen, Bookmark, Highlighter } from "lucide-react";
import { useFetch } from "../lib/useFetch";
import { asList } from "../lib/api";

function Section({ icon: Icon, title, empty, children, count, testid }) {
  return (
    <section className="border-t border-[var(--ink)] pt-5" data-testid={testid}>
      <p className="eyebrow flex items-center gap-2 mb-5"><Icon size={13} /> {title} · {count}</p>
      {count ? children : <p className="text-sm text-[var(--muted)]">{empty}</p>}
    </section>
  );
}

export default function MyLibrary() {
  const { data } = useFetch("/me/library", {});
  const progress = asList(data?.progress);
  const saved = asList(data?.saved);
  const highlights = asList(data?.highlights);
  const bookmarks = asList(data?.bookmarks);
  return (
    <div className="mt-14 space-y-12" data-testid="my-library">
      <h2 className="f-serif text-4xl">My library</h2>
      <Section icon={BookOpen} title="Continue reading" count={progress.length} empty="Open an issue in the online reader — we'll remember your page." testid="library-progress">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-5">
          {progress.map((p) => (
            <Link key={p.issue_id} to={`/reader/${p.issue_id}`} className="group" data-testid={`continue-${p.issue_id}`}>
              <div className="aspect-[2/3] overflow-hidden bg-[var(--paper-2)]"><img src={p.issue?.cover} alt="" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" /></div>
              <p className="f-serif text-lg mt-3 leading-tight">{p.issue?.title}</p>
              <div className="h-1 bg-[var(--line)] mt-2"><div className="h-full bg-[var(--red)]" style={{ width: `${Math.min(100, ((p.page + 1) / (p.total || 1)) * 100)}%` }} /></div>
              <p className="f-mono text-xs text-[var(--muted)] mt-1">Page {p.page + 1} of {p.total}</p>
            </Link>
          ))}
        </div>
      </Section>
      <Section icon={Bookmark} title="Saved studies" count={saved.length} empty="Use “Save study” on any article to keep it here." testid="library-saved">
        <ul className="divide-y rule">
          {saved.map((a) => <li key={a.slug} className="py-3"><Link to={`/article/${a.slug}`} className="f-serif text-xl link-u" data-testid={`saved-${a.slug}`}>{a.title}</Link><p className="f-mono text-xs text-[var(--muted)] mt-1">{a.author} · {a.category}</p></li>)}
        </ul>
      </Section>
      <Section icon={Highlighter} title="Highlights" count={highlights.length} empty="Select text in any article and choose Highlight." testid="library-highlights">
        <ul className="space-y-4">
          {highlights.slice(0, 20).map((h) => (
            <li key={h.id} className="border-l-2 border-[var(--red)] pl-4">
              <p className="f-serif text-lg leading-snug">“{h.text}”</p>
              <Link to={`/article/${h.slug}`} className="text-xs text-[var(--muted)] link-u">{h.title}</Link>
            </li>
          ))}
        </ul>
      </Section>
      <p className="text-sm text-[var(--muted)]" data-testid="library-bookmark-count">{bookmarks.length} page bookmark{bookmarks.length === 1 ? "" : "s"} across your issues — open the reader's bookmark list to jump back.</p>
    </div>
  );
}
